import React from 'react'
import { type InjectedDashIntlProps } from 'intl/intl.types'
import { injectIntl } from 'react-intl'
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryVoronoiContainer,
  VictoryScatter,
} from 'victory'

import {
  type ChartCoordinates,
  type ChartSeries,
  type Datum,
} from 'legacy/components/DataViz/dataViz.types'
import {
  getFormattedTime,
  getFormattedLabel,
  generateDomain,
  formatYTick,
  NO_DATA_LABEL,
} from 'legacy/components/DataViz/Charts/utils'
import useResizeListener from 'legacy/common/react/hooks/useResizeListener'
import {
  secondaryLineStyle,
  getScatterStyle,
  getLineStyle,
} from 'legacy/dash-components/line-chart-2/utils'
import { LineChartGradient } from 'legacy/dash-components/line-chart-2/components/LineChartGradient'
import {
  CHART_PADDING_RIGHT,
  chartContainerStyle,
  horizontalAxisStyle,
  verticalAxisStyle,
} from 'legacy/components/DataViz/Charts/CommonChartStyles'
import DotAndLabelTooltip from 'legacy/components/DataViz/Charts/DotAndLabelTooltip'

interface Props extends InjectedDashIntlProps {
  data: ChartCoordinates[][]
  series: ChartSeries[]
  dataType: 'currency' | 'number' | 'percent'
}

const LineChart: React.FC<Props> = props => {
  const [setDimensionRef, { width }] = useResizeListener(200)
  const { dataType, intl, series } = props
  const data = props.data.map(dataSet =>
    dataSet.map(datum => ({
      x: Date.parse(datum.x),
      y: datum.y === null ? null : Number(datum.y),
    }))
  )
  const domain = generateDomain(data, dataType)
  const isDay = domain.x[1] - domain.x[0] <= 86400000
  const scatterNames = series.map((_, i) => `scatter${i}`)

  return (
    <div ref={setDimensionRef}>
      <LineChartGradient data={series} />
      {width && (
        <VictoryChart
          padding={{ top: 10, bottom: 40, left: 80, right: CHART_PADDING_RIGHT }}
          domain={domain}
          width={width}
          height={300}
          domainPadding={{ x: 0, y: 1 }}
          scale={{ x: 'time' }}
          style={chartContainerStyle}
          containerComponent={
            <VictoryVoronoiContainer
              voronoiDimension="x"
              voronoiBlacklist={scatterNames}
              labels={(d: Datum) => (d.y !== null ? d.y.toString() : NO_DATA_LABEL)}
              labelComponent={
                <DotAndLabelTooltip
                  chartWidth={width}
                  series={series}
                  renderLabelText={(d: Datum) => getFormattedLabel(intl, dataType, isDay, d.y, d.x)}
                />
              }
            />
          }
        >
          <VictoryAxis
            tickFormat={x => getFormattedTime(intl, x, isDay)}
            style={horizontalAxisStyle(domain.x[0], domain.x[1])}
          />
          <VictoryAxis
            dependentAxis
            style={verticalAxisStyle}
            tickFormat={(t, index) => formatYTick(intl, dataType, domain.y[0], t, index)}
          />
          {series.map((n, i) =>
            n.type === 'last_period'
              ? // can't use fragments for victory chart children
                [
                  <VictoryLine name={n.type} style={secondaryLineStyle} data={data[i]} />,
                  <VictoryScatter
                    name={scatterNames[i]}
                    size={5}
                    data={data[i]}
                    style={getScatterStyle(n.type)}
                  />,
                ]
              : [
                  <VictoryArea name={n.type} data={data[i]} style={getLineStyle(n.type)} />,
                  <VictoryScatter
                    name={scatterNames[i]}
                    size={5}
                    data={data[i]}
                    style={getScatterStyle(n.type)}
                  />,
                ]
          )}
        </VictoryChart>
      )}
    </div>
  )
}

export default injectIntl(LineChart)
